import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Field, Loading } from "@/components/bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldAlert } from "lucide-react";
import { LogoSettings } from "@/components/LogoSettings";
import { DataCleanup } from "@/components/DataCleanup";

export default function Settings() {
  const { user, refreshBranding } = useApp();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === "admin";

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch((e) => toast.error(fmtErr(e))); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings", s);
      setS(data);
      refreshBranding();
      toast.success("Pengaturan tersimpan / 设置已保存");
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  if (!s) return <Loading />;

  return (
    <div className="space-y-5">
      <PageHeader id="Pengaturan Sistem" zh="系统设置" desc="Identitas perusahaan & target produksi." />
      {!isAdmin && (
        <div className="panel flex items-center gap-3 border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-300" data-testid="settings-admin-warning">
          <ShieldAlert className="h-5 w-5 shrink-0" /> Hanya admin dapat mengubah pengaturan. 仅管理员可修改
        </div>
      )}
      <div className="panel max-w-xl space-y-4 p-5" data-testid="settings-panel">
        <Field label="Nama Perusahaan"><Input value={s.companyName || ""} disabled={!isAdmin} onChange={(e) => setS({ ...s, companyName: e.target.value })} data-testid="settings-company-input" /></Field>
        <Field label="Tagline (tampil di halaman login)"><Input value={s.tagline ?? "Building Beyond Standards"} disabled={!isAdmin} onChange={(e) => setS({ ...s, tagline: e.target.value })} data-testid="settings-tagline-input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Label Periode"><Input value={s.periodLabel || ""} disabled={!isAdmin} onChange={(e) => setS({ ...s, periodLabel: e.target.value })} data-testid="settings-period-input" /></Field>
          <Field label="Target Tahunan (Ton)"><Input type="number" value={s.monthlyTargetTon ?? ""} disabled={!isAdmin} onChange={(e) => setS({ ...s, monthlyTargetTon: Number(e.target.value) })} data-testid="settings-target-input" /></Field>
        </div>
        <Field label="Target STB Bulanan (Ton) — garis target di grafik Trend STB">
          <Input type="number" min="0" step="0.1" value={s.stbTargetMonthlyTon ?? ""} disabled={!isAdmin} placeholder={s.monthlyTargetTon ? `kosong = ${(s.monthlyTargetTon / 12).toFixed(1)} (target tahunan / 12)` : "mis. 230"} onChange={(e) => setS({ ...s, stbTargetMonthlyTon: e.target.value === "" ? null : Number(e.target.value) })} data-testid="settings-stb-target-input" />
        </Field>
        <div className="rounded border border-slate-800 bg-[#0B1322] p-4 text-center" data-testid="settings-label-preview">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Preview label barcode</div>
          <div className="mt-1 font-display text-sm font-bold text-slate-100">{s.companyName}</div>
          <div className="cn text-xs text-slate-500">宏睿钢印印尼有限公司 · {s.periodLabel}</div>
        </div>
        {isAdmin && <Button onClick={save} disabled={busy} className="bg-sky-600 hover:bg-sky-500" data-testid="settings-save-button">Simpan Pengaturan / 保存</Button>}
      </div>
      <LogoSettings isAdmin={isAdmin} />
      {isAdmin && <DataCleanup />}
    </div>
  );
}